import * as React from "react";

import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { neutral, primary } from "app/themes/colors";
import Dialog from "components/dialog";

import KeyCap from "./key-cap.component";
import {
  closeQuickLauncherHelp,
  openQuickLauncher,
  selectQuickLauncherHelpOpen
} from "./quick-launcher.slice";
import {
  getQuickLauncherHotkeyLabel,
  isAppleDevice
} from "./use-quick-launcher-hotkey.hook";

/** The hotkey as separate caps: ["⌘", "K"] or ["Ctrl", "K"]. */
const hotkeyCaps = (): string[] =>
  isAppleDevice() ? ["⌘", "K"] : ["Ctrl", "K"];

function Row({
  icon,
  title,
  children
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack direction="row" gap={1.5} alignItems="flex-start">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: 1.5,
          backgroundColor: alpha(primary[400], 0.12),
          color: primary[500]
        }}
      >
        {icon}
      </Box>
      <Stack gap={0.25} sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {children}
        </Typography>
      </Stack>
    </Stack>
  );
}

function Keys({ children }: { children: string[] }) {
  return (
    <Stack
      component="span"
      direction="row"
      gap={0.5}
      sx={{ display: "inline-flex", verticalAlign: "middle", mx: 0.5 }}
    >
      {children.map((key) => (
        <KeyCap key={key}>{key}</KeyCap>
      ))}
    </Stack>
  );
}

/**
 * The "Keyboard shortcuts" guide — reached from the account menu (under
 * Release Notes) and from the launcher itself. Deliberately brief: one row per
 * idea, and a "Try it" that opens the launcher so the reader can learn by doing.
 *
 * Rendered from `QuickLauncherHosts`, so it exists exactly when the launcher
 * does — a flag-off user has neither the hotkey nor the menu item nor this.
 */
export default function QuickLauncherHelpDialog() {
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectQuickLauncherHelpOpen);

  const handleClose = () => dispatch(closeQuickLauncherHelp());
  const handleTryIt = () => {
    dispatch(closeQuickLauncherHelp());
    dispatch(openQuickLauncher("help"));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Keyboard shortcuts"
      maxWidth="sm"
      minWidth={520}
    >
      <Stack gap={2.5} data-testid="quick-launcher-help">
        <Row icon={<SearchIcon fontSize="small" />} title="Open the launcher">
          Press <Keys>{hotkeyCaps()}</Keys> on any page. Type to search actions,
          pages and patients — three or more characters searches patients by
          name, phone or ID. <Keys>{["↑", "↓"]}</Keys> to move,{" "}
          <Keys>{["↵"]}</Keys> to run, <Keys>{["esc"]}</Keys> to close.
        </Row>

        <Row
          icon={<PersonOutlineOutlinedIcon fontSize="small" />}
          title="On a patient chart"
        >
          The launcher knows who you are looking at. It adds a{" "}
          <strong>This patient</strong> section — new appointment, text, email,
          note, task, alert, invoice — plus a jump to every tab of the chart, so
          “hear” takes you to Hearing Aids without touching the mouse.
        </Row>

        <Row
          icon={<ReceiptOutlinedIcon fontSize="small" />}
          title="On an invoice"
        >
          A <strong>This invoice</strong> section appears above everything else:
          record a payment, send a pay-online link, issue a refund or an
          adjustment for that invoice.
        </Row>

        <Row
          icon={<EventAvailableOutlinedIcon fontSize="small" />}
          title="Anywhere"
        >
          New appointment opens the form right where you are — no detour to the
          scheduler. <strong>Screenshot this page</strong> frames what you are
          looking at and hands you a PNG to paste into a ticket. Drag the search
          bar to move the panel; it remembers where you left it.
        </Row>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ pt: 1, borderTop: `1px solid ${neutral[200]}` }}
        >
          <Typography variant="caption" color="text.secondary">
            Find this guide any time under your profile menu →{" "}
            <strong>Keyboard shortcuts</strong>.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={handleTryIt}
            data-testid="quick-launcher-help-try"
          >
            Try it — {getQuickLauncherHotkeyLabel()}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
